import {
  ArrowUpRight,
  Briefcase,
  Car,
  Code,
  Cpu,
  ExternalLink,
  Github,
  Lock, 
  Layers,
  Linkedin, Mail,
  Moon,
  Phone,
  Smartphone,
  Sparkles,
  Sun,
  Zap
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom';
import AnimatedBackground from './components/AnimatedBackground';
import AnimatedStripes from './components/AnimatedStripes';
import HeroMosaicImage from './components/HeroMosaicImage';
import Footer from './components/Footer';
import GlassEffect from './components/GlassEffect';
import RequestModal from './components/RequestModal';
import SEO from './components/SEO';
import AutomationPage from './pages/AutomationPage';
import DataManagementPage from './pages/DataManagementPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import MaryGuardianPage from './pages/MaryGuardianPage';
import AdBanner from './components/AdBanner';

const projects = [
  {
    id: "combi",
    title: "Combi.kz Food",
    descriptionKey: "projectCombiDesc",
    tech: ["Flutter", "Firebase", "REST API"],
    image: "/images/projects/combi.png",
    year: "2024",
    category: "FoodTech",
    links: { 
      github: "https://github.com/Khamit", 
      play: "https://play.google.com/store/apps/details?id=kz.combi.combi" 
    }
  },
  {
    id: "konder04",
    title: "Konder04.kz",
    descriptionKey: "projectKonderDesc",
    tech: ["React", "TypeScript"],
    image: "/images/projects/konder04.png",
    year: "2026",
    category: "Business",
    links: { 
      github: "https://github.com/Khamit",
      website: "https://konder04.kz"
    }
  },
  {
    id: "masterlock",
    title: "MasterLock",
    descriptionKey: "projectMasterlockDesc",
    tech: ["TypeScript", "VS Code API", "AES Encryption"],
    image: "/images/projects/masterlock.png",
    year: "2026",
    category: "DevTool",
    links: {
      github: "https://github.com/Khamit",
      marketplace: "https://marketplace.visualstudio.com/items?itemName=Khamit.masterlock"
    }
},
  {
    id: "bloom",
    title: "Bloom Daily",
    descriptionKey: "projectBloomDesc",
    tech: ["Swift", "SwiftData"],
    image: "/images/projects/bloom.png",
    year: "2025",
    category: "Productivity",
    links: { github: "https://github.com/Khamit" }
  },
];

const skills = ["Flutter", "Dart", "Firebase", "REST API", "C++", "Swift", "Node.js", "Git", "CI/CD", "React", "TypeScript", "BLoC/Riverpod", "SQL/NoSQL"];

const ProjectImage = ({ src, alt, title }) => {
  const [imgError, setImgError] = useState(false);
  
  if (imgError || !src) {
    let Icon = Code;
    if (title.includes('Konder04')) Icon = Car;
    else if (title.includes('Combi')) Icon = Smartphone;
    else if (title.includes('Ustam')) Icon = Briefcase;
    else if (title.includes('Bloom')) Icon = Sparkles;
    else if (title.includes('MasterLock')) Icon = Lock;
    
    return (
      <div className="h-48 bg-gradient-to-br from-fg to-accent flex items-center justify-center">
        <div className="text-center text --primary">
          <Icon size={48} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm font-medium">{title}</p>
        </div>
      </div>
    );
  }
  
  return (
    <img 
      src={src} 
      alt={alt}
      className="w-full h-48 object-cover"
      onError={() => setImgError(true)}
    />
  );
};

const FadeInSection = ({ children, delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => setIsVisible(entry.isIntersecting));
    });
    observer.observe(domRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={`transition-all duration-700 transform ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
      ref={domRef}
    >
      {children}
    </div>
  );
};

function HomePage({ darkMode, setDarkMode, changeLanguage, t, scrollToTop, scrollToSection }) {
  const location = useLocation();
  const scrollToContact = () => scrollToSection('contact');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState({ name: '', price: '' });

  useEffect(() => {
    const handleOpenModal = (event) => {
      setSelectedService(event.detail);
      setIsModalOpen(true);
    };
    
    window.addEventListener('openRequestModal', handleOpenModal);
    
    return () => {
      window.removeEventListener('openRequestModal', handleOpenModal);
    };
  }, []);

  useEffect(() => {
    if (location.state?.scrollTo === 'projects') {
      setTimeout(() => {
        scrollToSection('projects');
      }, 100);
    }
  }, [location, scrollToSection]);

  return (
    <>
      <SEO 
        title="COMBI.kz | IT Company & AI Automation in Kazakhstan"
        description="Mobile app development, websites, and AI automation solutions for business. Flutter, React, C++ development. Reduce repetitive work with AI agents."
        path="/"
      />

      <div className="min-h-screen relative z-10 text-fg transition-colors duration-500">
        <header className="fixed top-0 left-0 right-0 w-full z-50 --primary dark:bg-fg/80 backdrop-blur-md shadow-sm border-b border-fg/10">
          <div className="container mx-auto px-6 py-4 flex justify-between items-center">
            <button onClick={scrollToTop}
              className="hidden md:flex text-2xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent hover:opacity-80 transition-opacity cursor-pointer group items-center gap-2">
              COMBI<span className="text-fg/50">.kz</span>
              <Sparkles size={16} className="opacity-0 group-hover:opacity-100 transition-opacity text-primary" />
            </button>
            
            <div className="md:hidden"></div>
            
            <div className="flex gap-2 md:gap-4 items-center">
              <Link to="/automation" className="px-2 md:px-4 py-1 md:py-2 bg-gradient-to-r from-primary to-secondary text-bg rounded-lg hover:shadow-lg transition-all hover:scale-105 text-xs md:text-sm font-medium whitespace-nowrap">
                {t('automation')}
              </Link>
              
              <div className="flex gap-1 md:gap-2 items-center">
                <button onClick={() => changeLanguage('ru')} className="p-1 md:p-2 rounded-lg hover:bg-fg/10 transition">
                  <span className="fi fi-ru text-sm md:text-base"></span>
                </button>
                <button onClick={() => changeLanguage('en')} className="p-1 md:p-2 rounded-lg hover:bg-fg/10 transition">
                  <span className="fi fi-gb text-sm md:text-base"></span>
                </button>
                <button onClick={() => changeLanguage('kk')} className="p-1 md:p-2 rounded-lg hover:bg-fg/10 transition">
                  <span className="fi fi-kz text-sm md:text-base"></span>
                </button>
                <button onClick={() => setDarkMode(!darkMode)} className="p-1 md:p-2 rounded-lg bg-fg/10 backdrop-blur-sm hover:bg-fg/20 transition">
                  {darkMode ? <Sun size={16} className="md:w-5 md:h-5 text-accent" /> : <Moon size={16} className="md:w-5 md:h-5 text-fg/70" />}
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="pt-20">
          <FadeInSection delay={0}>
            <section className="container mx-auto px-6 py-20">
              <div className="flex flex-col md:flex-row items-center gap-12">
                <div className="flex-1 space-y-6">
                  <div className="inline-flex items-center gap-2 bg-primary/10 backdrop-blur-sm text-primary px-4 py-2 rounded-full border border-primary/20">
                    <Briefcase size={16} />
                    <span className="text-sm font-medium">{t('title')}</span>
                  </div>
                  <h1 className="text-5xl md:text-7xl font-bold text-fg leading-tight">
                    COMBI
                    <span className="text-primary relative inline-block">.kz</span>
                  </h1>
                  <p className="text-xl text-fg/60 max-w-xl leading-relaxed">{t('description')}</p>
                  <div className="flex gap-4 pt-4">
                    <a href="#contact" className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary to-accent text-fg rounded-xl hover:shadow-xl transition-all hover:scale-105">
                        {t('contactButton')}
                        <ArrowUpRight size={18} />
                      </a>
                    <a href="#projects" className="flex items-center gap-2 px-6 py-3 bg-fg/80 backdrop-blur-sm text-fg rounded-xl hover:bg-fg transition border border-bg/10">
                      {t('ourWorks')}
                    </a>
                  </div>
                </div>

<div className="flex-1 flex justify-center">
  <div className="relative group">
    <div className="absolute -inset-4 bg-gradient-to-r from-primary/30 to-secondary/30 blur-3xl opacity-40 group-hover:opacity-70 transition duration-700"></div>
    <div className="relative w-[280px] md:w-[340px] lg:w-[400px] aspect-[3/4] rounded-[2.5rem] overflow-hidden border-2 border-bg/50 dark:border-bg/20 shadow-2xl shadow-primary/20 hover:shadow-primary/40 transition-all duration-500 hover:scale-[1.02] bg-fg">
      
      {/* Мозаичная картинка */}
      <div className="absolute inset-0">
        <HeroMosaicImage
          src="/images/lordekz/ggi_50s.jpeg"
          alt="Combi.kz development"
          cols={6}
          rows={8}
          animationDuration={900}
          staggerDelay={35}
          startDelay={200}
        />
      </div>

      {/* Текстовый оверлей */}
      <div className="absolute bottom-0 left-0 right-0 p-6 z-10 pointer-events-none">
        <div className="text-center">
          <Code size={32} className="mx-auto mb-2 opacity-90 text-primary" />
          <p className="text-sm text-white/90 mt-2 leading-relaxed">
            <span className="font-bold text-primary">MaryGuardian</span> — {t('heroVideoText')}
          </p>
        </div>
      </div>

    </div>
  </div>
</div>
              </div>
            </section>
          </FadeInSection>

          <FadeInSection delay={100}>
            <section className="container mx-auto px-6 py-16">
              <h2 className="text-3xl font-bold mb-12 text-fg text-center flex items-center justify-center gap-2">
                <Zap size={28} className="text-primary" />
                {t('services')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="backdrop-blur-sm bg-bg/40 dark:bg-fg/10 border border-bg/20 dark:border-fg/10 rounded-xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group flex flex-col">
                  <Smartphone size={48} className="mx-auto mb-4 text-primary group-hover:scale-110 transition-transform" />
                  <h3 className="text-xl font-semibold mb-2 text-fg text-center">{t('service1')}</h3>
                  <p className="text-fg/60 text-sm text-center mb-4">{t('service1Desc')}</p>
                  <div className="flex-1" />
                  <button 
                    onClick={() => {
                      const modalEvent = new CustomEvent('openRequestModal', { 
                        detail: { planName: t('service1'), planPrice: '4500' }
                      });
                      window.dispatchEvent(modalEvent);
                    }}
                    className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-primary to-accent text-bg hover:shadow-xl transition-all hover:scale-105"
                  >
                    {t('orderButton')}
                    <ArrowUpRight size={18} />
                  </button>
                </div>

                <div className="backdrop-blur-sm bg-bg/40 dark:bg-fg/10 border border-bg/20 dark:border-fg/10 rounded-xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group flex flex-col">
                  <Code size={48} className="mx-auto mb-4 text-primary group-hover:scale-110 transition-transform" />
                  <h3 className="text-xl font-semibold mb-2 text-fg text-center">{t('service2')}</h3>
                  <p className="text-fg/60 text-sm text-center mb-4">{t('service2Desc')}</p>
                  <div className="flex-1" />
                  <button 
                    onClick={() => {
                      const modalEvent = new CustomEvent('openRequestModal', { 
                        detail: { planName: t('service2'), planPrice: '3800' }
                      });
                      window.dispatchEvent(modalEvent);
                    }}
                    className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-primary to-accent text-bg hover:shadow-xl transition-all hover:scale-105"
                  >
                    {t('orderButton')}
                    <ArrowUpRight size={18} />
                  </button>
                </div>

                <div className="backdrop-blur-sm bg-bg/40 dark:bg-fg/10 border border-bg/20 dark:border-fg/10 rounded-xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group flex flex-col">
                  <Cpu size={48} className="mx-auto mb-4 text-primary group-hover:scale-110 transition-transform" />
                  <h3 className="text-xl font-semibold mb-2 text-fg text-center">{t('service3')}</h3>
                  <p className="text-fg/60 text-sm text-center mb-4">{t('service3Desc')}</p>
                  <div className="flex-1" />
                  <button 
                    onClick={() => {
                      const modalEvent = new CustomEvent('openRequestModal', { 
                        detail: { planName: t('service3'), planPrice: '6500' }
                      });
                      window.dispatchEvent(modalEvent);
                    }}
                    className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-primary to-accent text-bg hover:shadow-xl transition-all hover:scale-105"
                  >
                    {t('orderButton')}
                    <ArrowUpRight size={18} />
                  </button>
                </div>
              </div>
            </section>
          </FadeInSection>

          <FadeInSection delay={200}>
            <section className="container mx-auto px-6 py-16">
              <div className="backdrop-blur-sm bg-bg/30 dark:bg-fg/10 rounded-2xl p-8 shadow-xl border border-bg/20 dark:border-fg/10">
                <h2 className="text-3xl font-bold mb-4 text-fg flex items-center gap-2">
                  <Layers size={28} className="text-secondary" />
                  {t('about')}
                </h2>
                <p className="text-fg/60 text-lg leading-relaxed mb-6">{t('aboutText')}</p>
                <div className="border-t border-fg/10 pt-6">
                  <h3 className="text-xl font-semibold mb-4 text-fg">{t('whyUs')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[t('why1'), t('why2'), t('why3'), t('why4')].map((text, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-fg/60 group">
                        <span className="text-primary text-lg">✓</span> 
                        <span className="group-hover:translate-x-1 transition-transform">{text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          </FadeInSection>

          <FadeInSection delay={50}>
            <section className="container mx-auto px-6 py-10">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <button onClick={() => scrollToSection('founder')} className="backdrop-blur-sm bg-bg/30 dark:bg-fg/10 rounded-2xl p-6 text-center border border-bg/20 dark:border-fg/10 group hover:border-primary/50 transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform">3+</div>
                  <div className="text-sm text-fg/60 font-medium">лет коммерческого опыта</div>
                  <div className="text-xs text-fg/50 mt-1">с 2023 года</div>
                  <div className="text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity mt-2">Подробнее →</div>
                </button>
                <button onClick={() => scrollToSection('projects')} className="backdrop-blur-sm bg-bg/30 dark:bg-fg/10 rounded-2xl p-6 text-center border border-bg/20 dark:border-fg/10 group hover:border-primary/50 transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform">5+</div>
                  <div className="text-sm text-fg/60 font-medium">завершенных проектов</div>
                  <div className="text-xs text-fg/50 mt-1">Web • Mobile • AI</div>
                  <div className="text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity mt-2">Смотреть проекты →</div>
                </button>
                <button onClick={() => scrollToSection('skills')} className="backdrop-blur-sm bg-bg/30 dark:bg-fg/10 rounded-2xl p-6 text-center border border-bg/20 dark:border-fg/10 group hover:border-primary/50 transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform">12+</div>
                  <div className="text-sm text-fg/60 font-medium">технологий в стеке</div>
                  <div className="text-xs text-fg/50 mt-1">Flutter • C++ • Swift • Firebase</div>
                  <div className="text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity mt-2">Технологии →</div>
                </button>
                <button onClick={() => scrollToSection('contact')} className="backdrop-blur-sm bg-bg/30 dark:bg-fg/10 rounded-2xl p-6 text-center border border-bg/20 dark:border-fg/10 group hover:border-primary/50 transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform">10+</div>
                  <div className="text-sm text-fg/60 font-medium">тысяч пользователей</div>
                  <div className="text-xs text-fg/50 mt-1">суммарно по проектам</div>
                  <div className="text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity mt-2">Связаться →</div>
                </button>
              </div>
            </section>
          </FadeInSection>

          <FadeInSection delay={400}>
            <section id="projects" className="container mx-auto px-6 py-16">
              <h2 className="text-3xl font-bold mb-12 text-fg text-center font-mono">/ PORTFOLIO / {new Date().getFullYear()}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {projects.map((project, idx) => (
                  <div key={idx} className="group backdrop-blur-sm bg-bg/40 dark:bg-fg/10 rounded-xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-white/20 dark:border-fg/10">
                    <div className="relative overflow-hidden">
                      <ProjectImage src={project.image} alt={project.title} title={project.title} />
                      <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm px-2 py-1 rounded-md text-primary text-xs font-mono">{project.year}</div>
                      <div className="absolute top-3 right-3 bg-primary/80 backdrop-blur-sm px-2 py-1 rounded-md text-primary text-xs font-mono">{project.category}</div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-fg mb-2 group-hover:text-primary transition-colors">{project.title}</h3>
                      <p className="text-fg/60 text-sm mb-4">{t(project.descriptionKey)}</p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {project.tech.map((tech, i) => (
                          <span key={i} className="text-xs px-2 py-1 bg-fg/10 backdrop-blur-sm rounded-md text-fg/70 font-mono">{tech}</span>
                        ))}
                      </div>
                      <div className="flex gap-4 pt-2 border-t border-fg/10 flex-wrap">
                        {project.links.website && (
                          <a href={project.links.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-fg/70 hover:text-primary transition font-medium group/link">
                            <ExternalLink size={16} /> {t('visitWebsite')}
                            <ArrowUpRight size={12} className="opacity-0 group-hover/link:opacity-100 transition" />
                          </a>
                        )}
                        {project.links.github && (
                          <a href={project.links.github} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-fg/70 hover:text-primary transition font-medium group/link">
                            <Github size={16} /> {t('viewCode')}
                            <ArrowUpRight size={12} className="opacity-0 group-hover/link:opacity-100 transition" />
                          </a>
                        )}
                        {project.links.play && (
                          <a href={project.links.play} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-fg/70 hover:text-primary transition font-medium group/link">
                            <ExternalLink size={16} /> {t('GooglePlay')}
                            <ArrowUpRight size={12} className="opacity-0 group-hover/link:opacity-100 transition" />
                          </a>
                        )}
                        {project.links.marketplace && (
                          <a href={project.links.marketplace} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-fg/70 hover:text-primary transition font-medium group/link">
                            <ExternalLink size={16} /> {t('viewMarketplace')}
                            <ArrowUpRight size={12} className="opacity-0 group-hover/link:opacity-100 transition" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </FadeInSection>

          <FadeInSection delay={500}>
            <section id="skills" className="container mx-auto px-6 py-16">
              <h2 className="text-3xl font-bold mb-12 text-fg text-center font-mono">TECH STACK</h2>
              <div className="flex flex-wrap justify-center gap-3">
                {skills.map((skill, i) => (
                  <span key={i} className="px-4 py-2 backdrop-blur-sm bg-primary/10 text-primary rounded-full font-medium hover:bg-primary/20 transition-all hover:scale-105 cursor-default border border-primary/20">
                    #{skill}
                  </span>
                ))}
              </div>
            </section>
          </FadeInSection>

          <FadeInSection delay={600}>
            <section id="contact" className="container mx-auto px-6 py-16 mb-12">
              <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-8 text-center text-bg shadow-xl backdrop-blur-sm">
                <h2 className="text-3xl font-bold mb-4">{t('ready')}</h2>
                <p className="mb-6 opacity-90">{t('contactText')}</p>
                <div className="flex flex-wrap justify-center gap-4">
                  <a href="mailto:khamit@combi.kz" className="flex items-center gap-2 px-6 py-3 bg-bg backdrop-blur-sm text-fg rounded-xl hover:shadow-lg transition hover:scale-105">
                    <Mail size={20} /> {t('email')}
                  </a>
                  <a href="tel:+77712292371" className="flex items-center gap-2 px-6 py-3 bg-fg/20 rounded-xl hover:bg-fg/30 transition hover:scale-105 backdrop-blur-sm">
                    <Phone size={20} /> {t('phone')}
                  </a>
                  <a href="https://github.com/Khamit" target="_blank" className="flex items-center gap-2 px-6 py-3 bg-fg/20 rounded-xl hover:bg-fg/30 transition hover:scale-105 backdrop-blur-sm">
                    <Github size={20} /> GitHub
                  </a>
                  <a href="https://www.linkedin.com/in/khamit-lordekz/" target="_blank" className="flex items-center gap-2 px-6 py-3 bg-fg/20 rounded-xl hover:bg-fg/30 transition hover:scale-105 backdrop-blur-sm">
                    <Linkedin size={20} /> LinkedIn
                  </a>
                </div>
              </div>
            </section>
          </FadeInSection>
          <AdBanner />
        </main>

        
        <Footer />
      </div>
      <RequestModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        planName={selectedService.name}
        planPrice={selectedService.price}
      />
    </>
  );
}

function App() {
  const { t, i18n } = useTranslation();

  const getInitialTheme = () => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme !== null) {
      return savedTheme === 'dark';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  };

  const [darkMode, setDarkMode] = useState(getInitialTheme);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    if (window.location.hash) {
      window.history.pushState("", document.title, window.location.pathname);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
  };

  const scrollToContact = () => scrollToSection('contact');

  return (
    <Router>
        {/* Фоновый слой */}
        <AnimatedBackground darkMode={darkMode} />
        
      <GlassEffect
        darkMode={darkMode}
        panelCount={8}
        panelWidth={160}
        opacity={0.15}
        speed={1.3}
        gap={20}
        blur={10}
        // Кастомные цвета
        bgColor="rgba(99, 102, 241, 0)" // Индиго
        borderColor="rgba(255, 255, 255, .4)"
        borderRadius="0"
        borderWidth="2px"
        // Кастомные тени
        shadowColor="rgba(99, 102, 241, 0.2)"
        shadowBlur={0}
        shadowOffsetY={0}
        shadowOpacity={0.4}
      />


      {/* Основной контент */}
      <div className="relative z-10">
        <Routes>
          <Route path="/" element={
            <HomePage 
              darkMode={darkMode}
              setDarkMode={setDarkMode}
              changeLanguage={changeLanguage}
              t={t}
              scrollToTop={scrollToTop}
              scrollToSection={scrollToSection}
            />
          } />
          <Route path="/mary-guardian" element={
              <MaryGuardianPage 
                darkMode={darkMode} 
                setDarkMode={setDarkMode} 
                changeLanguage={changeLanguage} 
              />
            } />
          <Route path="/automation" element={
            <AutomationPage 
              darkMode={darkMode}
              setDarkMode={setDarkMode}
              changeLanguage={changeLanguage}
              t={t}
              scrollToTop={scrollToTop}
              scrollToContact={scrollToContact}
            />
          } />
          <Route path="/manage-data" element={
            <DataManagementPage />
          } />
          <Route path="/privacy-policy" element={
            <PrivacyPolicyPage />
          } />
        </Routes>
      </div>
    </Router>
  );
}

export default App;